import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CUTOUT_PROCESSING_LEASE_MS,
  FAILED_RETURNS_UNLESS_REMADE,
  STALLED_BILLING_NOTICE,
  UNMADE_SHEET_RETURN_NOTICE,
  failureMessage,
  imageStatusLabel,
  imageSubline,
  jobProgress,
  parseServerDate,
  pendingImages,
  progressCountLabel,
  refundableTotal,
  totalFailureAmountNotice,
  type DetailJobView,
} from "@makit/shared";

import { PageHead } from "../../app/PageHead";
import { Button, ProgressBar, Skeleton, StatusChip } from "../../components/primitives";
import { LimitBanner } from "../../components/banners";
import { EmptyState } from "../../components/feedback";
import { getJob, processJob } from "../../lib/api";
import { rejectionMessage } from "../../lib/edit-messages";
import type { ShellJob } from "../../lib/shell-job";

/** `W-24` 만드는 중. 앱 `A-24`·`A-24b`·`A-23` 이 여기 모이고, `A-24c`·`A-24d` 를 PRD `7.7.10`
 * 표대로 받는다.
 *
 * **웹에서 시작한 작업은 웹에서 끝까지 보여야 한다.** 폰으로만 진행 상황을 볼 수 있으면
 * PC 로 시작한 셀러가 폰을 꺼내야 하고, 기기를 넘나드는 것이 선택이 아니라 의무가 된다.
 *
 * ## 예시 갈래를 걷었다 (2026-10-04)
 *
 * 그전까지 이 화면은 **진짜 작업에서도 예시 숫자**(`5 / 7장`, `12분째`, `6장 모두`)를 그렸고
 * 서버를 다시 묻지 않았다. 같은 날 진짜 작업은 `LiveMaking` 이 받게 갈랐고, 이어서 사용자
 * 지시로 **예시 갈래(`SampleMaking`) 자체를 걷었다.** 예시 작업이 없어졌다(셸이 모든 id 를
 * 서버에 묻는다). ⛔ 예시 숫자를 이 화면에 되살리지 마라.
 *
 * ## ⭐ 판정 · 낱말 · 동작은 앱 `job/[jobId].tsx` + `lib/making.ts` 와 같다
 *
 * 이 제품의 정상 동선이 **폰에서 올리고 PC에서 이어받기**라 셀러가 두 화면을 나란히 본다
 * (PRD `7.7.10`). 판정 구역(파일 끝)이 앱 `makingState` 와 같은 순서로 같은 자리를 고르고,
 * `scripts/web-making.test.mjs` 가 앱 함수를 실제로 불러 두 답을 맞댄다. */
export function MakingStage() {
  const job = useOutletContext<ShellJob | undefined>();
  // 셸이 작업을 받은 뒤에만 이 화면이 선다. 그래도 비면 그리지 않는다.
  if (!job) return null;
  // `key` 는 작업을 옮겨 다닐 때 잠금(`inFlight`)과 오류 줄이 다음 작업에 남지 않게 한다.
  return <LiveMaking key={job.id} jobId={job.id} />;
}

/* ── 진짜 작업 ─────────────────────────────────────────────────────────────── */

/** 멈춤 판정 문턱. **앱 `A-24b` 의 `STALL_AFTER_MS` 와 같은 값을 같은 상수에서 읽는다.**
 *
 * ⛔ 서버 임대(`CUTOUT_PROCESSING_LEASE_MS`)보다 짧게 두지 마라(PRD `7.6.7a`). 임대가 끝나야
 * `다시 확인` 이 멈춘 장을 풀 수 있다. 그보다 먼저 멈췄다고 말하면 **멈췄다고 말하면서 풀어
 * 주지는 못하는 창**이 생기고, 그 창에서 누른 셀러는 버튼이 죽었다고 읽는다. */
const STALL_AFTER_MS = CUTOUT_PROCESSING_LEASE_MS;

/** 멈춤 판정은 데이터가 안 바뀌어도 시간이 지나면 바뀐다. 그 시간을 다시 읽는 간격.
 *  서버를 부르지 않는다(화면만 다시 그린다). */
const STALL_CLOCK_MS = 15_000;

function LiveMaking({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // `refetchInterval` 이 뮤테이션의 최신 상태를 읽어야 하는데, 훅 둘이 서로를 가리키면 타입
  // 추론이 순환한다. 앱과 같은 방법(ref)으로 끊는다. **두 번 누르기를 막는 문도 이 ref 다.**
  const inFlightRef = useRef(false);

  const query = useQuery({
    // ⭐ 셸과 **같은 키**다. 이 구독이 받아 온 것을 셸의 작업 바(`5 / 8장 · 만드는 중`)도
    //    그대로 읽고, 작업이 `준비됨` 이 되면 `StageIndex` 가 결과 확인으로 옮긴다.
    queryKey: ["job", jobId],
    queryFn: () => getJob(jobId),
    refetchInterval: (q) =>
      makingPollInterval(q.state.data?.status, inFlightRef.current) ?? false,
    // ⭐ **탭이 숨으면 멈춘다.** 보지 않는 화면을 2초마다 묻지 않는다. 돌아오면
    //    react-query 의 창 포커스 갱신이 한 번 묻는다.
    refetchIntervalInBackground: false,
  });

  /** `만들기 시작`(`A-24d` · 시작한 적 없는 작업). **장당 과금 경로다.**
   *
   * 비용: 한 번 누르면 `POST /process` 한 번이다. 서버가 남은 `대기` 장을 시간 예산 안에서
   * 만든다(회차당 두어 장, 장마다 공급자 원가). 그 앞에서 임대가 끝난 장을 되찾기도 한다
   * (되찾은 장은 공급자를 한 번 더 부른다. 상한은 서버의 `AUTO_RECOVERY_CALL_LIMIT`).
   * 셀러에게 더 청구되는 돈은 없다. 결제에 이미 들어 있는 장이다.
   *
   * ⛔ **멈춘 작업의 `다시 확인` 은 이 길이 아니다.** 그쪽은 `GET` 한 번이고 원가가 없다
   *    (앱 `A-24b` 와 같다). 서버의 작업 조회가 임대가 끝난 장을 대기로 돌리거나 못 만든
   *    장으로 끝낸다. 그 뒤에 남은 장이 있으면 이 버튼이 선다.
   * ⛔ **화면이 스스로 다시 부르지 않는다.** 미룬 장이 남아도 자동으로 이어 걸지 않는다
   *    (`lib/api.ts` 의 `processJob` 머리말). 누를 때마다 한 번이고, 도는 동안은 잠긴다.
   * ⛔ **뮤테이션의 `isPending` 만으로 잠그지 않는다.** 그 값은 다음 그리기에서야 바뀌므로
   *    빠른 두 번 누르기가 같은 그리기 안에서 두 요청을 낸다. `inFlightRef` 가 실제 문이다. */
  const start = useMutation({
    mutationFn: () => processJob(jobId),
    onMutate: () => {
      inFlightRef.current = true;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(["job", jobId], next);
    },
    onSettled: (_data, error) => {
      inFlightRef.current = false;
      // 거절(409 등)이면 서버 쪽 상태가 화면과 달라졌다는 뜻이다. 다시 묻는다.
      if (error) void queryClient.invalidateQueries({ queryKey: ["job", jobId] });
    },
  });

  const view = query.data ?? null;
  const running = view?.status === "PROCESSING";

  // 멈춤은 시간이 정한다. 데이터가 그대로여도 문턱을 넘으면 화면이 바뀌어야 한다.
  const [, setClock] = useState(0);
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setClock((n) => n + 1), STALL_CLOCK_MS);
    return () => window.clearInterval(timer);
  }, [running]);

  function pressStart() {
    if (inFlightRef.current) return;
    start.mutate();
  }

  /** `A-24b` 의 `다시 확인`. **앱과 같이 다시 묻기만 한다**(원가 없음). */
  function pressRecheck() {
    if (query.isFetching) return;
    void query.refetch();
  }

  if (!view) {
    // 셸이 작업을 받은 뒤에만 이 화면이 서므로 거의 오지 않는 자리다. 빈 자리만 그린다.
    return (
      <>
        <PageHead frame="W-24" title="만드는 중" />
        <ul className="files" style={{ marginTop: 16 }}>
          {[0, 1, 2].map((i) => (
            <li className="file" key={`sk-${i}`}>
              <Skeleton width="52%" />
              <Skeleton width="38%" height={8} />
              <StatusChip tone="idle" label="대기" />
            </li>
          ))}
        </ul>
      </>
    );
  }

  const progress = jobProgress(view);
  const idleMs = elapsedSince(view.updatedAt);
  const facts: LiveMakingFacts = {
    status: view.status,
    total: progress.total,
    done: progress.done,
    failed: progress.failed,
    pending: pendingImages(view).length,
    // 결제 화면으로 보낼지 가르는 값이다(`sendsToPaidPanel`). 장마다의 호출 횟수를 더한다.
    providerCalls: view.images.reduce((n, image) => n + image.providerCallCount, 0),
    idleMs,
    stallAfterMs: STALL_AFTER_MS,
    requestInFlight: start.isPending,
  };
  const phase = liveMakingPhase(facts);

  // ⭐ **결제만 하고 한 장도 손대지 않은 작업은 `A-28`(결제됨 · 시작 전)이 받는다.** 웹의
  //    `A-28` 은 `W-26` 의 결제됨 패널이고(PRD `7.7` 표: `A-28` 의 웹 자리는 「작업의 상태」가
  //    고르는 단계 모드 본문), 거기에 **`지금 취소하면 전액 돌아갑니다` / `시작된 뒤에는 취소가
  //    안 됩니다`** 경고가 선다. 여기서 `만들기 시작` 을 바로 주면 그 경고를 건너뛴다.
  //    ⚠️ 본래는 셸의 `stageForStatus` 가 고를 일이다(`PAID` 를 통째로 `MAKING` 으로 보낸다).
  //    그 파일이 다른 작업 자리라 여기서 옮긴다.
  if (sendsToPaidPanel(phase, facts)) return <Navigate to="pay" replace />;

  const stalled = phase === "STALLED";
  const remaining = remainingPages(progress);
  const counts = { done: progress.done, remaining };
  const title = liveMakingTitle(phase, counts);
  const banners = liveMakingBanners(phase, counts);
  // 앱과 같은 조건이다. 도는 중이거나 남은 장이 없으면 그 실패는 낡은 신호라 숨긴다.
  const failure =
    showsStartFailure({ isError: start.isError, running, remaining })
      ? rejectionMessage(start.error, "시작하")
      : null;

  return (
    <>
      <PageHead
        frame="W-24"
        title="만드는 중"
        right={progressCountLabel(view)}
      />

      <div className="review" style={{ marginTop: 16 }}>
        <div>
          {/* 전부 실패면 막대가 할 말이 없다. 오른쪽 `전부 실패했을 때` 가 말한다(앱 `A-23` 도
              목록 위 카드를 걷는다). */}
          {title !== null ? (
            <div className="setgrp">
              <div className="toggle" style={{ paddingBottom: 7 }}>
                <span style={{ fontWeight: 600 }}>{title}</span>
                <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                  {progressCountLabel(view)}
                </span>
              </div>
              {/* 비율은 shared 의 `jobProgress` 다. 못 만든 장도 결판난 장이라 분자에 든다. */}
              <ProgressBar ratio={progress.ratio} label="배경 지우기 진행" />
              <p className="hint">{liveMakingNote(phase)}</p>
            </div>
          ) : null}

          <ul className="files" style={{ marginTop: title !== null ? 12 : 0 }}>
            {view.images.map((image) => {
              // 칩과 보조줄에 **같은 `stalled`** 를 넘긴다. 하나만 넘기면 한 행이 두 판정을
              // 들고 선다(앱 `A-24b` 와 같은 규칙).
              const chip = imageStatusLabel(image.status, { stalled });
              return (
                <li className="file" key={image.id}>
                  {image.status === "PENDING" ? (
                    // 아직 오지 않은 장은 빈 자리로 그린다(목업 `W-24` 셋째 행).
                    <>
                      <Skeleton width="52%" />
                      <Skeleton width="38%" height={8} />
                    </>
                  ) : (
                    <>
                      {/* 서버는 파일명을 저장하지 않는다. 앱과 같이 장 번호로 부른다. */}
                      <span className="file-name mono">{`${image.sortOrder + 1}번 사진`}</span>
                      <span className="file-dim mono">{imageSubline(image, { stalled })}</span>
                    </>
                  )}
                  <StatusChip tone={chip.tone} label={chip.label} />
                </li>
              );
            })}
          </ul>
        </div>

        <div className="editpanel">
          {phase === "TOTAL_FAILURE" ? (
            <LiveTotalFailure view={view} onPickFiles={() => navigate("/jobs")} />
          ) : stalled ? (
            <section>
              <h3>{minutes(idleMs)}분째 멈춰 있을 때</h3>
              {/* ⛔ 「“기다리세요”만 있으면…」 설계 메모(ScopeBanner)를 걷었다 (2026-10-04).
                  문장은 앱 `A-24b` 와 **글자 그대로 같다**. 뒷가지까지다(`D-76`). */}
              <p className="hint">
                {minutes(idleMs)}분째면 뭔가 걸린 것입니다. 다시 확인을 누르면 멈춘 장이
                대기로 바뀌거나 못 만든 장으로 끝납니다.
              </p>
              <LimitBanner icon="✓">
                먼저 만든 {progress.done}장은 그대로 있습니다. 다시 올리지 않아도 됩니다.
              </LimitBanner>
              <div style={{ marginTop: 9 }}>
                <LimitBanner icon="₩">
                  <span className="l1">{STALLED_BILLING_NOTICE}</span>
                  <span className="l2">{FAILED_RETURNS_UNLESS_REMADE}</span>
                </LimitBanner>
              </div>
              {/* ⛔ `문의하기` 를 두지 않는다(PRD `7.13` D-60 · `7.7.3a`). */}
              <div style={{ display: "flex", gap: 8, marginTop: 9, flexWrap: "wrap-reverse" }}>
                <Button
                  label="다시 확인"
                  small
                  disabled={query.isFetching}
                  onClick={pressRecheck}
                />
              </div>
            </section>
          ) : (
            <section>
              {/* 배너는 상태가 고른다(앱 `makingBanners` 와 같은 글자). 여기서 짓지 않는다. */}
              {banners.map((banner, i) => (
                <div key={`${banner.icon}-${i}`} style={{ marginTop: i === 0 ? 0 : 9 }}>
                  <LimitBanner icon={banner.icon}>
                    {banner.l2 ? (
                      <>
                        <span className="l1">{banner.text}</span>
                        <span className="l2">{banner.l2}</span>
                      </>
                    ) : (
                      banner.text
                    )}
                  </LimitBanner>
                </div>
              ))}
              {/* ⭐ **남은 장을 다시 미는 웹의 길이 이 버튼이다**(`A-24d`). 서버 `POST /process`
                  는 시간 예산 안에서 장을 미루고(`대기` 로 남김) 이어 걸지 않는다. */}
              {canStart(phase) ? (
                <div style={{ display: "flex", gap: 8, marginTop: 9, flexWrap: "wrap-reverse" }}>
                  <Button
                    label="만들기 시작"
                    small
                    disabled={start.isPending}
                    onClick={pressStart}
                  />
                </div>
              ) : null}
              {failure ? (
                <p className="hint" role="status">
                  {failure.text}
                </p>
              ) : null}
            </section>
          )}
        </div>
      </div>

      {/* ⛔ 발밑 「이 화면에서 아직 못 합니다」(`MakingCantDo`, 미정 배지)를 걷었다 (2026-10-04
          사용자 지시). 멈춘 장이 `다시 확인` 으로 풀린다는 말은 멈춤 패널의 설명 줄이 한다. */}
    </>
  );
}

/** 전부 실패했을 때(`A-23`). 숫자와 금액, 원인 문장이 진짜 작업에서 온다. */
function LiveTotalFailure({ view, onPickFiles }: { view: DetailJobView; onPickFiles: () => void }) {
  // 원인 문장은 앱 `A-23` 과 같은 자리에서 읽는다. 첫 장의 코드, 없으면 작업의 코드.
  const message = failureMessage(view.images[0]?.errorCode ?? view.errorCode);
  // ⛔ **장수에 단가를 곱하지 않는다**(`refundableTotal` 머리말). 하나라도 금액을 모르면
  //    금액을 빼고 말한다. `₩0` 을 지어내지 않는다. 앱 `A-23` 과 같은 판정이다.
  const refund = refundableTotal(view);
  const amount = refund.imageCount > 0 && refund.unknownCount === 0 ? refund.amount : null;
  return (
    <section>
      <h3>전부 실패했을 때</h3>
      <EmptyState
        art="!"
        tone="bad"
        title={`${view.images.length}장 모두 배경을 못 지웠어요`}
        body={message.detail}
      />
      <LimitBanner icon="₩">
        <span className="l1">{totalFailureAmountNotice(amount)}</span>
        {/* 2026-10-04 확정(PRD `7.0` 「로그인 전 화면의 확정 문구」 (5) 1. 환불정책 B안). 청구 내역
            아래 안내(`Account.tsx`)와 같은 상수다. 옛 `실행 방식 미정` 배지는 걷었다. */}
        <span className="l2">{UNMADE_SHEET_RETURN_NOTICE}</span>
      </LimitBanner>
      {/* 폰은 `다시 찍기`, 웹은 `파일 다시 고르기`다. PC 에는 카메라가 없다.
          ⭐ `파일 다시 고르기` 는 앱 `다시 찍기` 처럼 **새 작업**을 여는 자리로 간다. 웹에서
          파일을 고르는 곳은 `내 작업`(`W-20`)의 올리기 패널 하나다. 같은 작업에 다시 담지
          않는다. 결제 뒤에는 서버가 담기를 막는다(`UPLOAD_CLOSED_AFTER_PAYMENT`).
          ⚠️ `폰에서 이어서 하기` 는 **웹에서 폰으로 넘기는 길이 아직 없어** 꺼 둔다. 꺼진 사유
          문장은 정본(목업 `W-24`)에 없어 여기서 짓지 않았다. */}
      <div style={{ display: "flex", gap: 8, marginTop: 9, flexWrap: "wrap-reverse" }}>
        <Button label="파일 다시 고르기" variant="ghost" small onClick={onPickFiles} />
        <Button label="폰에서 이어서 하기" small disabled />
      </div>
      {/* ⛔ 「이 화면에서 아직 못 합니다. 같은 사진으로 다시 만들기.」를 걷었다 (2026-10-04 사용자
          지시). 셀러에게 할 일을 주지 않는 설계 메모였다. */}
    </section>
  );
}

function elapsedSince(serverTime: string): number {
  const at = parseServerDate(serverTime)?.getTime();
  return at ? Date.now() - at : 0;
}

function minutes(ms: number): number {
  return Math.max(1, Math.floor(ms / 60_000));
}

/* ── 판정 시작 (live-making-rules) ───────────────────────────────────────────
 *
 * ⛔ **이 구역은 import 를 쓰지 않는다. JSX 도 쓰지 않는다.** `scripts/web-making.test.mjs`
 * 가 이 구역만 잘라 타입을 걷고 실제로 불러 본다. 그리고 **앱 `lib/making.ts` 를 함께 불러
 * 같은 사실에 같은 답을 내는지 맞댄다.** 무엇을 말할지 고르는 일이 JSX 의 삼항 사슬 안에
 * 있으면 틀린 상태에 맞는 문장이 붙어도 시험이 못 잡는다. 글자는 파일 안에 그대로 있기
 * 때문이다. */

/** 진짜 작업이 설 수 있는 자리. **이름과 순서가 앱 `MakingState` 와 같다.** 웹만의 자리는
 *  `TOTAL_FAILURE` 하나다(앱은 그 상태를 같은 파일의 다른 갈래 `A-23` 으로 그린다).
 *
 * - `STALLED` — `A-24b`. 작업이 잠겨 있는데 임대가 끝나도록 갱신이 없다.
 * - `LIVE` — `A-24`. **이 화면이 건 요청이 지금 살아 있다.** 그때만 「지우고 있다」가 참이다.
 * - `LOCKED` — `A-24c`. 잠겨 있는데 이 화면의 요청은 없다. **지금 도는지 화면은 모른다.**
 * - `RESUMABLE` — `A-24d`. 잠금이 풀렸고 남은 장이 있다. 누를 때까지 아무것도 안 일어난다.
 * - `NOT_STARTED` — 한 장도 만들지 않았고 남은 장이 있다.
 * - `TOTAL_FAILURE` — `A-23`. 한 장도 못 만들었다.
 * - `SETTLED` — 남은 장이 없다. 셸이 곧 결과 확인으로 옮긴다. */
export type LiveMakingPhase =
  | "STALLED"
  | "LIVE"
  | "LOCKED"
  | "RESUMABLE"
  | "NOT_STARTED"
  | "TOTAL_FAILURE"
  | "SETTLED";

export interface LiveMakingFacts {
  /** 서버 작업 상태(`DetailJobStatus`). */
  status: string;
  total: number;
  done: number;
  failed: number;
  /** `대기` 장 수. `만들기 시작` 이 집어갈 장이다(shared `canStartProcessing` 의 축). */
  pending: number;
  /** 작업 행이 마지막으로 바뀐 뒤 지난 시간. */
  idleMs: number;
  /** 멈춤 문턱. 서버 임대와 같은 값을 넘긴다. */
  stallAfterMs: number;
  /** 이 화면이 건 `만들기 시작` 요청이 살아 있는가. */
  requestInFlight: boolean;
  /** 이 작업의 장들이 공급자에 나간 횟수의 합(`JobImageView.providerCallCount` 합). */
  providerCalls: number;
}

/** **아직 결과를 못 받은 장** = 전체 − 만든 장 − 못 만든 장. 공급자에 나가 있는 장도 든다.
 *  음수로 내려가지 않는다(앱 `remainingPages` 와 같은 규칙). */
export function remainingPages(counts: { total: number; done: number; failed: number }): number {
  return Math.max(0, counts.total - counts.done - counts.failed);
}

/** 사실에서 자리를 고른다. **순서가 규칙이고, 앱 `makingState` 와 같은 순서다.** */
export function liveMakingPhase(facts: LiveMakingFacts): LiveMakingPhase {
  // 앱은 전부 실패(`A-23`)를 상태 판정보다 먼저 가른다. 같은 자리에서 가른다.
  if (facts.total > 0 && facts.failed === facts.total) return "TOTAL_FAILURE";
  const running = facts.status === "PROCESSING";
  // 갇힌 것이 먼저다. 갇힌 동안에도 요청은 살아 있을 수 있다.
  if (running && facts.idleMs > facts.stallAfterMs) return "STALLED";
  // **우리 요청이 살아 있을 때만 「지우고 있다」가 참이다.**
  if (facts.requestInFlight) return "LIVE";
  const remaining = remainingPages(facts);
  // 잠겨 있는데 우리 요청은 없다. 살아 있는 회차인지 끊긴 회차인지 화면은 구분하지 못한다.
  // ⛔ **`LIVE` 로 올리지 마라**(PRD `7.7.10`). 폰은 `4장이 남았어요` 라고 하는데 PC 가
  //    `배경을 지우고 있어요` 라고 하면 나란히 보는 셀러에게 두 화면이 다른 말을 한다.
  if (running) return remaining > 0 ? "LOCKED" : "SETTLED";
  // shared `canStartProcessing` 과 같은 축: 지워진 작업이 아니고 `대기` 장이 있다.
  const canStartNow = facts.status !== "DISCARDED" && facts.pending > 0;
  if (canStartNow && remaining > 0) return facts.done > 0 ? "RESUMABLE" : "NOT_STARTED";
  return "SETTLED";
}

/** `만들기 시작` 을 그리는가. 앱 액션바의 `canStart` 갈래와 같다. */
export function canStart(phase: LiveMakingPhase): boolean {
  return phase === "RESUMABLE" || phase === "NOT_STARTED";
}

/** **결제만 하고 한 장도 손대지 않은 작업은 `W-26` 의 결제됨 패널(`A-28`)로 보낸다.**
 *  그 패널이 시작 전 경고(`지금 취소하면 전액 돌아갑니다`)를 한다.
 *
 * ⭐ **「손대지 않았다」는 장 상태가 아니라 공급자 호출로 잰다.** 멈춘 장이 되찾아지면 다시
 * `대기` 가 되어 만든 장 0 · 못 만든 장 0 인 작업이 생기는데, 그 작업은 **이미 공급자에
 * 나갔다.** 서버의 시작 전 취소도 `providerCallCount = 0` 을 조건으로 건다
 * (`apps/api/src/lib/payment-cancel.ts`, feat/cancel-before-start). 장 상태만 보고 보내면
 * 결제 화면이 **서버가 들어주지 않을 취소를 약속한다.** 그 작업은 여기 남아 앱처럼
 * `아직 시작하지 않았어요` / `만들기 시작` 을 그린다. */
export function sendsToPaidPanel(phase: LiveMakingPhase, facts: LiveMakingFacts): boolean {
  return (
    phase === "NOT_STARTED" &&
    facts.status === "PAID" &&
    facts.done === 0 &&
    facts.failed === 0 &&
    facts.providerCalls === 0
  );
}

/** `만들기 시작` 실패 줄을 보이는가. **앱과 같은 조건**이다. 도는 중이거나 남은 장이 없으면
 *  그 실패는 낡은 신호다(클라이언트에서만 끊겨도 서버는 계속 처리했을 수 있다). */
export function showsStartFailure(input: { isError: boolean; running: boolean; remaining: number }): boolean {
  return input.isError && !input.running && input.remaining > 0;
}

/** 새로 묻는 간격. `null` 이면 묻지 않는다. **앱 `job/[jobId].tsx` 의 `refetchInterval` 과 같은
 *  간격, 같은 조건**이다(`scripts/web-making.test.mjs` 가 두 숫자를 맞대 본다) — 서버가 일하는 동안
 *  (`PROCESSING`)이거나 이 화면이 건 요청이 살아 있는 동안만 2초마다 묻는다.
 *  ⛔ `PAID`·`UPLOADED`·끝난 상태는 묻지 않는다. 바뀔 일이 없는데 묻는 것은 낭비다. */
export function makingPollInterval(status: string | undefined, requestInFlight: boolean): number | null {
  return requestInFlight || status === "PROCESSING" ? 2_000 : null;
}

/** 카드 제목. `null` 이면 카드를 그리지 않는다(전부 실패 · 남은 장 없음).
 *
 * - `LIVE` · `STALLED` 는 **목업 `W-24` 의 카드 제목 그대로**다. 멈춤은 오른쪽 패널이 말한다
 *   (PRD `W-24` 우 패널 1. 앱 `A-24b` 의 `n장에서 멈춰 있어요` 를 웹 카드로 옮기는 표는 정본에
 *   없다).
 * - `LOCKED` · `RESUMABLE` 은 PRD `7.7.10` 표가 `W-24` 에 준 제목이다(앱 `A-24c`·`A-24d`).
 * - `NOT_STARTED` 는 앱 `makingTitle` 과 같은 글자다.
 * ⛔ 새 문장을 짓지 않는다. */
export function liveMakingTitle(
  phase: LiveMakingPhase,
  counts: { done: number; remaining: number },
): string | null {
  switch (phase) {
    case "LIVE":
    case "STALLED":
      return "배경을 지우고 있어요";
    case "LOCKED":
    case "RESUMABLE":
      return `${counts.remaining}장이 남았어요`;
    case "NOT_STARTED":
      return "아직 시작하지 않았어요";
    // 남은 장이 없는 자리는 셸이 곧 결과 확인으로 옮긴다. 그 찰나에 지을 말이 정본에 없다.
    case "SETTLED":
    case "TOTAL_FAILURE":
      return null;
  }
}

/** 카드 설명 줄.
 *
 * `탭을 닫아도 계속됩니다` 는 **서버가 도는 동안만** 참이다. 미룬 장이 남아 아무것도 돌지
 * 않을 때 그 말을 하면 셀러는 기다리기만 한다. 그래서 `W-24` 의 긴 판은 `LIVE`·`STALLED`
 * (목업 `W-24` 카드 그대로)에만 쓰고, 나머지는 앱 `makingNote` 와 같은 글자다.
 * ⛔ 지난 시간도 남은 시간도 여기 적지 않는다. `n분째` 는 오른쪽 멈춤 패널의 말이다. */
export function liveMakingNote(phase: LiveMakingPhase): string {
  if (phase === "LIVE" || phase === "STALLED") return "한 장씩 차례로 만듭니다. 탭을 닫아도 계속됩니다.";
  // ⭐ `A-24d` 의 요점이 이 한 문장이다(PRD `7.7.10`). 버튼은 있었고 없던 것은 그 말이었다.
  if (phase === "RESUMABLE") return "만들기 시작을 누르면 남은 장부터 만듭니다.";
  return "한 장씩 차례로 만듭니다.";
}

export interface LiveMakingBanner {
  icon: string;
  /** 첫 줄(한 줄 배너면 전문). */
  text: string;
  /** 둘째 줄. 목업의 `l1`/`l2` 두 줄 배너일 때만. */
  l2?: string;
}

/** 오른쪽 패널의 제한 배너(멈춤 · 전부 실패 밖의 자리).
 *
 * ⭐ **PRD `7.7.10` 표가 `W-24` 에 준 것은 `A-24c`·`A-24d` 의 `!`·`₩` 배너 둘이다.** 글자는 앱
 * `makingBanners` 의 `RESUMABLE` 갈래와 같다(시험이 맞댄다). `₩` 배너는 목업 `A-24d` 처럼
 * 두 줄이다. ⚠️ **`LOCKED`(`A-24c`)에는 달지 않는다.** 그 둘은 목업 `A-24d` 프레임에만 있고,
 * `₩` 배너가 `만들기 시작을 다시 눌러도` 라고 말하는데 `LOCKED` 에는 그 버튼이 없다.
 * ⛔ **앱의 `↓` 배너(`진행은 이 화면에서만 보입니다`)는 옮기지 않는다.** PRD `W-24` 절이
 *    「폰은 그 말을 `↓` 카드가 따로 하고 웹은 카드 설명 줄이 한다」고 갈라 뒀다. 그래서
 *    `RESUMABLE` 밖에는 배너가 없다. */
export function liveMakingBanners(
  phase: LiveMakingPhase,
  _counts: { done: number; remaining: number },
): LiveMakingBanner[] {
  if (phase !== "RESUMABLE") return [];
  return [
    { icon: "!", text: "한 번에 다 만들지 못하면 남은 장이 또 생깁니다. 고장이 아닙니다." },
    {
      icon: "₩",
      text: "만들기 시작을 다시 눌러도 더 내실 금액이 없습니다.",
      l2: "남은 장은 이번 작업에 이미 들어 있고, 못 만든 장은 받는 금액이 없습니다.",
    },
  ];
}

/* ── 판정 끝 (live-making-rules) ─────────────────────────────────────────── */
